export const TENANT_COMPANY_NAME_MAX = 50;
export const TENANT_SLUG_MAX = 48;

const COMPANY_NAME_VALUE = /^[\p{L}\p{N}\s&.\-']+$/u;
const SLUG_VALUE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function sanitizeTenantCompanyName(raw: string): string {
  return raw
    .replace(/[^\p{L}\p{N}\s&.\-']/gu, '')
    .slice(0, TENANT_COMPANY_NAME_MAX);
}

export function sanitizeTenantSlug(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TENANT_SLUG_MAX);
}

export function validateTenantCompanyName(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Company name is required';
  if (trimmed.length > TENANT_COMPANY_NAME_MAX) {
    return `Company name must be ${TENANT_COMPANY_NAME_MAX} characters or less`;
  }
  if (!COMPANY_NAME_VALUE.test(trimmed)) {
    return 'Use letters, numbers, spaces, and & . - \' only';
  }
  return undefined;
}

export function validateTenantSlug(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Slug is required';
  if (trimmed.length > TENANT_SLUG_MAX) {
    return `Slug must be ${TENANT_SLUG_MAX} characters or less`;
  }
  if (!SLUG_VALUE.test(trimmed)) {
    return 'Use lowercase letters, numbers, and hyphens only (no spaces)';
  }
  return undefined;
}

export function validateTenantSubscriptionPlan(value: string): string | undefined {
  if (!value.trim()) return 'Select a subscription plan';
  return undefined;
}

export function validateTenantSubscriptionExpiry(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const picked = new Date(`${value}T00:00:00`);
  if (Number.isNaN(picked.getTime())) return 'Enter a valid expiry date';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (picked < today) return 'Expiry date cannot be in the past';
  return undefined;
}

/** Date display hint for tenant forms (India-first). */
export function tenantDateFormatHint(country?: string): string {
  return country === 'US' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
}

export function tenantDatePickerLocale(country?: string): string {
  if (country === 'US') return 'en';
  if (country === 'IN') return 'en-in';
  return 'en-gb';
}

export function tenantDatePickerFormat(country?: string): string {
  return country === 'US' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
}

const DOMAIN_VALUE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
const SUBDOMAIN_VALUE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** Strip protocol/path and keep hostname-safe characters. */
export function sanitizeTenantDomain(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .replace(/[^a-z0-9.-]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 253);
}

/** Single DNS label for tenant subdomain (e.g. client). */
export function sanitizeTenantSubdomain(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function validateTenantDomain(value: string): string | undefined {
  const raw = value.trim();
  if (!raw) return undefined;
  if (/\s/.test(raw)) return 'Domain cannot contain spaces';

  // Accept optional protocol and strip path, but validate hostname strictly.
  const withoutProto = raw.replace(/^https?:\/\//i, '');
  const hostWithPort = withoutProto.split('/')[0] || '';
  const host = hostWithPort.replace(/:\d+$/, '').toLowerCase();

  if (!host) return 'Enter a valid domain (e.g. app.client.com)';
  if (!DOMAIN_VALUE.test(host)) return 'Enter a valid domain (e.g. app.client.com)';
  return undefined;
}

export function validateTenantSubdomain(value: string): string | undefined {
  const raw = value.trim().toLowerCase();
  if (!raw) return undefined;
  if (/\s/.test(value)) return 'Subdomain cannot contain spaces';
  if (!SUBDOMAIN_VALUE.test(raw)) return 'Use lowercase letters, numbers, and hyphens only (e.g. client)';
  return undefined;
}

/** First DNS label of a hostname (e.g. app.client.com → app). */
export function tenantDomainFirstLabel(domain: string): string {
  const host = sanitizeTenantDomain(domain);
  if (!host) return '';
  return host.split('.')[0] || '';
}

/**
 * Optional fields:
 * - Domain only, or subdomain only → OK (mutually exclusive usage)
 * - Both set → subdomain must match the domain’s first label (combined config)
 */
export function validateTenantDomainConfig(
  domain: string,
  subdomain: string,
): { domain?: string; subdomain?: string } {
  const domainErr = validateTenantDomain(domain);
  const subdomainErr = validateTenantSubdomain(subdomain);
  const out: { domain?: string; subdomain?: string } = {};
  if (domainErr) out.domain = domainErr;
  if (subdomainErr) out.subdomain = subdomainErr;
  if (out.domain || out.subdomain) return out;

  const d = sanitizeTenantDomain(domain);
  const s = sanitizeTenantSubdomain(subdomain);
  if (!d || !s) return out;

  const first = tenantDomainFirstLabel(d);
  if (first !== s) {
    const msg = `When both are set, subdomain must match the domain prefix (“${first}” for ${d})`;
    out.domain = msg;
    out.subdomain = msg;
  }
  return out;
}
